import { useState } from "react";
import { Plus, Edit, Trash2, Star, ExternalLink, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  useAllPortfolioProjects,
  useCreatePortfolioProject,
  useUpdatePortfolioProject,
  useDeletePortfolioProject,
  PortfolioProject,
} from "@/hooks/usePortfolioProjects";
import { useServices } from "@/hooks/useServices";

export const PortfolioProjectsSection = () => {
  const { data: projects, isLoading } = useAllPortfolioProjects();
  const { data: services } = useServices();
  const createProject = useCreatePortfolioProject();
  const updateProject = useUpdatePortfolioProject();
  const deleteProject = useDeletePortfolioProject();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<PortfolioProject | null>(null);
  const [formData, setFormData] = useState({
    service_id: "",
    title: "",
    title_en: "",
    title_ru: "",
    description: "",
    description_en: "",
    description_ru: "",
    image_url: "",
    project_url: "",
    client_name: "",
    completion_date: "",
    technologies: [] as string[],
    is_featured: false,
    is_active: true,
    order_index: 0,
  });
  const [newTechnology, setNewTechnology] = useState("");

  const handleOpenDialog = (project?: PortfolioProject) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        service_id: project.service_id,
        title: project.title,
        title_en: project.title_en || "",
        title_ru: project.title_ru || "",
        description: project.description || "",
        description_en: project.description_en || "",
        description_ru: project.description_ru || "",
        image_url: project.image_url,
        project_url: project.project_url || "",
        client_name: project.client_name || "",
        completion_date: project.completion_date || "",
        technologies: project.technologies || [],
        is_featured: project.is_featured,
        is_active: project.is_active,
        order_index: project.order_index,
      });
    } else {
      setEditingProject(null);
      setFormData({
        service_id: "",
        title: "",
        title_en: "",
        title_ru: "",
        description: "",
        description_en: "",
        description_ru: "",
        image_url: "",
        project_url: "",
        client_name: "",
        completion_date: "",
        technologies: [],
        is_featured: false,
        is_active: true,
        order_index: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingProject) {
      await updateProject.mutateAsync({
        id: editingProject.id,
        ...formData,
      });
    } else {
      await createProject.mutateAsync(formData);
    }
    
    setDialogOpen(false);
  };

  const handleAddTechnology = () => {
    if (newTechnology.trim()) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, newTechnology.trim()],
      });
      setNewTechnology("");
    }
  };

  const handleRemoveTechnology = (index: number) => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter((_, i) => i !== index),
    });
  };

  const handleDelete = async (id: string) => {
    if (confirm("Վստա՞հ եք, որ ցանկանում եք ջնջել այս նախագիծը:")) {
      await deleteProject.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <div>Բեռնվում է...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold">Պորտֆոլիո նախագծեր</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Ավելացնել նախագիծ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProject ? "Խմբագրել նախագիծը" : "Նոր պորտֆոլիո նախագիծ"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="service_id">Ծառայություն *</Label>
                <Select
                  value={formData.service_id}
                  onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ընտրել ծառայություն" />
                  </SelectTrigger>
                  <SelectContent>
                    {services?.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="title">Անվանում (հայերեն) *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="title_en">Անվանում (անգլերեն)</Label>
                  <Input
                    id="title_en"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="title_ru">Անվանում (ռուսերեն)</Label>
                  <Input
                    id="title_ru"
                    value={formData.title_ru}
                    onChange={(e) => setFormData({ ...formData, title_ru: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="image_url">Նկարի URL *</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_url">Նախագծի URL</Label>
                  <Input
                    id="project_url"
                    type="url"
                    value={formData.project_url}
                    onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="client_name">Հաճախորդի անուն</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="completion_date">Ավարտման ամսաթիվ</Label>
                  <Input
                    id="completion_date"
                    type="date"
                    value={formData.completion_date}
                    onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="order_index">Հերթականություն</Label>
                  <Input
                    id="order_index"
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Նկարագրություն (հայերեն)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="description_en">Նկարագրություն (անգլերեն)</Label>
                <Textarea
                  id="description_en"
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="description_ru">Նկարագրություն (ռուսերեն)</Label>
                <Textarea
                  id="description_ru"
                  value={formData.description_ru}
                  onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })}
                  rows={3}
                />
              </div>

              <div>
                <Label>Տեխնոլոգիաներ</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTechnology}
                    onChange={(e) => setNewTechnology(e.target.value)}
                    placeholder="Նոր տեխնոլոգիա"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTechnology();
                      }
                    }}
                  />
                  <Button type="button" onClick={handleAddTechnology}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technologies.map((tech, index) => (
                    <Badge key={index} variant="outline" className="gap-1">
                      {tech}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={() => handleRemoveTechnology(index)}
                      >
                        ×
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                  />
                  <Label htmlFor="is_featured">Featured նախագիծ</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Ակտիվ</Label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Չեղարկել
                </Button>
                <Button type="submit">
                  {editingProject ? "Պահպանել" : "Ավելացնել"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {projects?.map((project: any) => (
          <Card key={project.id} className="overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              <div className="h-44 w-full flex-shrink-0 sm:h-48 sm:w-48">
                <img
                  src={project.image_url}
                  alt={project.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col p-4">
                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h3 className="break-words text-base font-semibold sm:text-lg">{project.title}</h3>
                      {project.is_featured && (
                        <Badge variant="default" className="gap-1">
                          <Star className="h-3 w-3" />
                          Featured
                        </Badge>
                      )}
                      {!project.is_active && (
                        <Badge variant="secondary">Անակտիվ</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Ծառայություն: {project.services?.title}
                    </p>
                    {project.client_name && (
                      <p className="text-sm mb-2">
                        Հաճախորդ: <span className="font-medium">{project.client_name}</span>
                      </p>
                    )}
                    {project.completion_date && (
                      <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(project.completion_date).toLocaleDateString('hy-AM')}
                      </p>
                    )}
                    {project.description && (
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {project.description}
                      </p>
                    )}
                    {project.technologies && project.technologies.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {project.technologies.map((tech: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {project.project_url && (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Դիտել նախագիծը
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="flex w-full shrink-0 justify-end gap-2 sm:w-auto sm:justify-start">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(project)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(project.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {projects?.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          Պորտֆոլիո նախագծեր դեռ ավելացված չեն
        </Card>
      )}
    </div>
  );
};
